export default function Privacy() {
  return (
    <div style={{ minHeight: "100vh", padding: "60px 24px" }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <h1 style={h1}>Privacy Policy</h1>
        <p style={meta}>Effective date: May 25, 2025</p>

        <Section title="1. Overview">
          <p style={body}>
            WorldRTT ("we", "us", or "our") operates worldrtt.com (the
            "Service"). This Privacy Policy explains what personal information we
            collect, how we use it, and your rights regarding that information.
            By using the Service, you agree to the collection and use of
            information as described here.
          </p>
          <p style={body}>
            WorldRTT is operated as a personal project by an individual based in
            British Columbia, Canada, and is subject to the{" "}
            <em>Personal Information Protection and Electronic Documents Act</em>{" "}
            (PIPEDA) and applicable provincial privacy law.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p style={body}>
            <strong style={{ color: "#EFF2F5" }}>Account information:</strong>{" "}
            When you register, we collect your email address and, if you use
            Google sign-in, your name and profile information from your Google
            account.
          </p>
          <p style={body}>
            <strong style={{ color: "#EFF2F5" }}>Probe data:</strong> URLs you
            submit for testing, probe results (latency measurements, HTTP status
            codes, response headers), and timestamps. Authenticated users' probe
            history is stored and associated with their account.
          </p>
          <p style={body}>
            <strong style={{ color: "#EFF2F5" }}>Anonymous usage data:</strong>{" "}
            For users who are not signed in, we store a temporary fingerprint
            derived from your IP address and browser information solely to
            enforce rate limits. This data is deleted within 24 hours and is
            not linked to any identity.
          </p>
          <p style={body}>
            <strong style={{ color: "#EFF2F5" }}>Payment information:</strong>{" "}
            Billing is handled by Stripe. We do not store credit card numbers
            or raw payment details. We store a payment provider customer
            identifier and your subscription status to manage your plan tier.
          </p>
          <p style={body}>
            <strong style={{ color: "#EFF2F5" }}>Usage and log data:</strong>{" "}
            Standard server logs including IP addresses, request timestamps,
            browser user agents, and error events. These are used for security,
            abuse prevention, and debugging.
          </p>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul style={list}>
            <li>To provide and operate the Service</li>
            <li>To manage your account and subscription</li>
            <li>To enforce rate limits and detect abuse</li>
            <li>To send transactional emails (receipts, account alerts)</li>
            <li>To respond to support requests</li>
            <li>To improve the Service based on aggregate usage patterns</li>
          </ul>
          <p style={body}>
            We do not sell your personal information to third parties. We do not
            use your probe URLs or results for advertising purposes.
          </p>
        </Section>

        <Section title="4. Data Sharing">
          <p style={body}>
            We share data only with the following categories of service
            providers, strictly to operate the Service. Each provider processes
            data only as necessary for the function described.
          </p>
          <ul style={list}>
            <li>
              <strong style={{ color: "#EFF2F5" }}>Authentication provider</strong>{" "}
              — manages user sign-in, session tokens, and identity
            </li>
            <li>
              <strong style={{ color: "#EFF2F5" }}>Stripe</strong>{" "}
              — payment processing and subscription management. Stripe's privacy
              policy governs how they handle your payment information.
            </li>
            <li>
              <strong style={{ color: "#EFF2F5" }}>Database provider</strong>{" "}
              — hosts the database that stores probe history and account data
            </li>
            <li>
              <strong style={{ color: "#EFF2F5" }}>Cloudflare</strong>{" "}
              — network infrastructure, content delivery, and distributed probe
              execution. All traffic passes through Cloudflare's network.
            </li>
            <li>
              <strong style={{ color: "#EFF2F5" }}>Hosting provider</strong>{" "}
              — operates the servers that run the Service's backend
            </li>
            <li>
              <strong style={{ color: "#EFF2F5" }}>Email delivery provider</strong>{" "}
              — sends transactional emails such as account alerts and receipts
            </li>
          </ul>
          <p style={body}>
            We may disclose data if required by applicable law, court order, or
            government authority, or to protect the rights, property, or safety
            of WorldRTT or its users.
          </p>
        </Section>

        <Section title="5. Custom Request Headers and Sensitive Data">
          <p style={body}>
            When you include custom request headers in a probe (for example,
            API keys or authorization tokens), those headers are encrypted at
            rest before being stored. We recommend against probing endpoints
            with production credentials and suggest rotating any sensitive
            values used for testing.
          </p>
        </Section>

        <Section title="6. Data Retention">
          <p style={body}>
            Probe history for authenticated users is retained for the duration
            of your account. Anonymous probe fingerprints used for rate-limiting
            are deleted within 24 hours of creation.
          </p>
          <p style={body}>
            Account data is retained until you delete your account. To request
            deletion of your account or personal data, contact us at{" "}
            <a href="mailto:privacy@worldrtt.com" style={link}>
              privacy@worldrtt.com
            </a>
            . After deletion, we may retain anonymized, non-identifiable
            aggregate data.
          </p>
        </Section>

        <Section title="7. Cookies and Local Storage">
          <p style={body}>
            We use cookies and browser local storage to maintain your
            authenticated session. These are essential to the operation of the
            Service. We do not use third-party advertising cookies or cross-site
            tracking technologies.
          </p>
        </Section>

        <Section title="8. Security">
          <p style={body}>
            We implement industry-standard security measures, including
            encryption of data in transit and encryption at rest for sensitive
            stored information. No system is perfectly secure. If you discover a
            security vulnerability, please report it responsibly to{" "}
            <a href="mailto:security@worldrtt.com" style={link}>
              security@worldrtt.com
            </a>
            .
          </p>
        </Section>

        <Section title="9. Your Rights">
          <p style={body}>
            Depending on your jurisdiction, you may have the right to access,
            correct, or delete your personal information, withdraw consent,
            restrict or object to processing, or request data portability. To
            exercise any of these rights, contact us at{" "}
            <a href="mailto:privacy@worldrtt.com" style={link}>
              privacy@worldrtt.com
            </a>
            . We will respond within a reasonable time and in accordance with
            applicable law.
          </p>
        </Section>

        <Section title="10. Children's Privacy">
          <p style={body}>
            The Service is not directed to children under 13. We do not
            knowingly collect personal information from children under 13. If
            you believe a child has provided us with personal information,
            contact us and we will delete it promptly.
          </p>
        </Section>

        <Section title="11. Changes to This Policy">
          <p style={body}>
            We may update this Privacy Policy periodically. We will notify you
            of material changes via email or a notice on the Service. The
            "Effective date" at the top of this page indicates when the policy
            was last revised. Continued use of the Service after changes take
            effect constitutes acceptance of the revised policy.
          </p>
        </Section>

        <Section title="12. Contact">
          <p style={body}>
            Questions or concerns about this Privacy Policy? Contact us at{" "}
            <a href="mailto:privacy@worldrtt.com" style={link}>
              privacy@worldrtt.com
            </a>
            .
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="glass" style={{ padding: "24px 28px", marginBottom: 16 }}>
      <h2 style={h2}>{title}</h2>
      {children}
    </div>
  );
}

const h1: React.CSSProperties = {
  fontSize: 32,
  fontWeight: 700,
  color: "#EFF2F5",
  margin: "0 0 8px",
};
const meta: React.CSSProperties = {
  color: "#98A1AB",
  fontSize: 14,
  margin: "0 0 32px",
};
const h2: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: "#FF2566",
  margin: "0 0 12px",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};
const body: React.CSSProperties = {
  color: "#EFF2F5",
  fontSize: 15,
  lineHeight: 1.7,
  margin: "0 0 10px",
};
const list: React.CSSProperties = {
  color: "#EFF2F5",
  fontSize: 15,
  lineHeight: 1.9,
  paddingLeft: 20,
  margin: "0 0 10px",
};
const link: React.CSSProperties = {
  color: "#FF2566",
  textDecoration: "none",
};
